/* New PDP layout: mobile gallery slider + "added to bag" nudge */

/*
 * Full screen image viewer (opens when a gallery image is clicked / tapped).
 * - click / tap / double tap toggles zoom, wheel and pinch zoom, drag to pan when zoomed
 * - swipe left / right (not zoomed), arrow buttons and arrow keys to change image, Esc / X / dark area to close
 */
class PdpZoom {
  constructor(list, start, onClose) {
    this.list = list;
    this.index = start;
    this.onClose = onClose;
    this.MAX = 4;
    this.scale = 1;
    this.tx = 0;
    this.ty = 0;
    this.pointers = new Map();
    this.opener = document.activeElement;
    this.build();
    this.show(start);
  }

  build() {
    const icon = (d) => '<svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true"><path d="' + d + '" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    const multi = this.list.length > 1;
    const root = document.createElement('div');
    root.className = 'pdp-zoom';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-label', 'Zoom');
    root.innerHTML =
      '<div class="pdp-zoom__bar"><span class="pdp-zoom__count"></span>' +
      '<button type="button" class="pdp-zoom__btn" data-zoom-close aria-label="Close">' + icon('M3 3l12 12M15 3L3 15') + '</button></div>' +
      '<div class="pdp-zoom__stage"><img class="pdp-zoom__img" alt="" draggable="false"></div>' +
      (multi
        ? '<button type="button" class="pdp-zoom__btn pdp-zoom__nav pdp-zoom__nav--prev" data-zoom-prev aria-label="Previous">' + icon('M11 3L5 9l6 6') + '</button>' +
          '<button type="button" class="pdp-zoom__btn pdp-zoom__nav pdp-zoom__nav--next" data-zoom-next aria-label="Next">' + icon('M7 3l6 6-6 6') + '</button>'
        : '');
    document.body.appendChild(root);
    document.documentElement.classList.add('pdp-zoom-lock');

    this.root = root;
    this.stage = root.querySelector('.pdp-zoom__stage');
    this.img = root.querySelector('.pdp-zoom__img');
    this.count = root.querySelector('.pdp-zoom__count');
    this.prevBtn = root.querySelector('[data-zoom-prev]');
    this.nextBtn = root.querySelector('[data-zoom-next]');

    root.querySelector('[data-zoom-close]').addEventListener('click', () => this.close());
    this.prevBtn?.addEventListener('click', () => this.go(this.index - 1));
    this.nextBtn?.addEventListener('click', () => this.go(this.index + 1));

    this.onKey = (event) => {
      if (event.key === 'Escape') this.close();
      else if (event.key === 'ArrowLeft') this.go(this.index - 1);
      else if (event.key === 'ArrowRight') this.go(this.index + 1);
    };
    document.addEventListener('keydown', this.onKey);

    const s = this.stage;
    s.addEventListener('pointerdown', (e) => this.down(e));
    s.addEventListener('pointermove', (e) => this.move(e));
    s.addEventListener('pointerup', (e) => this.up(e));
    s.addEventListener('pointercancel', (e) => this.up(e, true));
    s.addEventListener('wheel', (e) => {
      e.preventDefault();
      const p = this.rel(e.clientX, e.clientY);
      this.zoomAt(p.x, p.y, this.scale * Math.exp(-e.deltaY * 0.0025));
    }, { passive: false });

    setTimeout(() => root.classList.add('is-open'), 20);
    root.querySelector('[data-zoom-close]').focus({ preventScroll: true });
  }

  // point relative to the centre of the stage
  rel(x, y) {
    const r = this.stage.getBoundingClientRect();
    return { x: x - (r.left + r.width / 2), y: y - (r.top + r.height / 2) };
  }

  show(i) {
    this.index = i;
    const item = this.list[i];
    this.scale = 1;
    this.tx = 0;
    this.ty = 0;
    this.img.classList.remove('is-animating');
    this.img.alt = item.alt || '';
    this.img.src = item.thumb; // already cached, shows instantly while the large version loads
    const big = new Image();
    big.onload = () => { if (this.index === i) this.img.src = item.src; };
    big.src = item.src;
    this.apply();
    this.count.textContent = this.list.length > 1 ? (i + 1) + ' / ' + this.list.length : '';
    if (this.prevBtn) this.prevBtn.disabled = i <= 0;
    if (this.nextBtn) this.nextBtn.disabled = i >= this.list.length - 1;
    [i - 1, i + 1].forEach((n) => { if (this.list[n]) new Image().src = this.list[n].src; });
  }

  go(i) {
    if (i < 0 || i > this.list.length - 1 || i === this.index) return;
    this.show(i);
  }

  clamp() {
    const w = this.img.offsetWidth * this.scale;
    const h = this.img.offsetHeight * this.scale;
    const maxX = Math.max(0, (w - this.stage.clientWidth) / 2);
    const maxY = Math.max(0, (h - this.stage.clientHeight) / 2);
    this.tx = Math.max(-maxX, Math.min(maxX, this.tx));
    this.ty = Math.max(-maxY, Math.min(maxY, this.ty));
  }

  apply() {
    this.clamp();
    this.img.style.transform = 'translate(-50%, -50%) translate(' + this.tx + 'px, ' + this.ty + 'px) scale(' + this.scale + ')';
    this.stage.classList.toggle('is-zoomed', this.scale > 1.001);
  }

  // zoom to a new scale keeping the point (px, py) under the finger / cursor
  zoomAt(px, py, next, animate) {
    next = Math.max(1, Math.min(this.MAX, next));
    const ratio = next / this.scale;
    this.tx = px - (px - this.tx) * ratio;
    this.ty = py - (py - this.ty) * ratio;
    this.scale = next;
    if (next === 1) { this.tx = 0; this.ty = 0; }
    this.img.classList.toggle('is-animating', !!animate);
    this.apply();
  }

  down(e) {
    try { this.stage.setPointerCapture(e.pointerId); } catch (err) { /* synthetic or already released pointer */ }
    this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    this.moved = this.pointers.size > 1;
    if (this.pointers.size === 1) {
      this.startX = e.clientX;
      this.startY = e.clientY;
      this.startT = Date.now();
    } else if (this.pointers.size === 2) {
      const [a, b] = Array.from(this.pointers.values());
      this.pinch = { dist: Math.hypot(a.x - b.x, a.y - b.y), scale: this.scale };
    }
    this.img.classList.remove('is-animating');
  }

  move(e) {
    const prev = this.pointers.get(e.pointerId);
    if (!prev) return;
    const cur = { x: e.clientX, y: e.clientY };
    this.pointers.set(e.pointerId, cur);

    if (this.pointers.size === 2 && this.pinch) {
      const [a, b] = Array.from(this.pointers.values());
      const mid = this.rel((a.x + b.x) / 2, (a.y + b.y) / 2);
      this.zoomAt(mid.x, mid.y, this.pinch.scale * (Math.hypot(a.x - b.x, a.y - b.y) / this.pinch.dist));
      this.moved = true;
      return;
    }

    if (Math.hypot(cur.x - this.startX, cur.y - this.startY) > 6) this.moved = true;
    if (this.scale > 1.001) {
      this.tx += cur.x - prev.x;
      this.ty += cur.y - prev.y;
      this.stage.classList.add('is-dragging');
      this.apply();
    }
  }

  up(e, cancelled) {
    if (!this.pointers.has(e.pointerId)) return;
    this.pointers.delete(e.pointerId);
    this.stage.classList.remove('is-dragging');
    if (this.pointers.size) {
      // a pinch ended with one finger still down: carry on panning from here without a jump
      this.pinch = null;
      return;
    }
    this.pinch = null;
    if (cancelled) return;

    const dx = e.clientX - this.startX;
    const dy = e.clientY - this.startY;
    if (!this.moved) {
      // tap / click: dark area closes, the image toggles zoom
      const r = this.img.getBoundingClientRect();
      const onImage = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
      if (!onImage && this.scale <= 1.001) { this.close(); return; }
      const p = this.rel(e.clientX, e.clientY);
      this.zoomAt(p.x, p.y, this.scale > 1.001 ? 1 : 2.5, true);
    } else if (this.scale <= 1.001 && Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      this.go(this.index + (dx < 0 ? 1 : -1));
    } else if (this.scale <= 1.001 && dy > 90 && Math.abs(dy) > Math.abs(dx) * 1.5) {
      this.close(); // swipe down to dismiss
    }
  }

  close() {
    if (this.closed) return;
    this.closed = true;
    document.removeEventListener('keydown', this.onKey);
    this.root.classList.remove('is-open');
    document.documentElement.classList.remove('pdp-zoom-lock');
    setTimeout(() => this.root.remove(), 200);
    this.onClose?.(this.index);
    if (this.opener && this.opener.focus) this.opener.focus({ preventScroll: true });
  }
}

if (!customElements.get('ss-pdp-gallery')) {
  customElements.define('ss-pdp-gallery', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;

      this.thumbsEl = this.querySelector('[data-thumbs]');
      this.thumbs = Array.from(this.querySelectorAll('[data-thumb]'));
      this.prev = this.querySelector('[data-nav="prev"]');
      this.next = this.querySelector('[data-nav="next"]');
      this.index = 0;

      this.onScroll = () => {
        cancelAnimationFrame(this.raf);
        this.raf = requestAnimationFrame(() => this.sync());
      };
      this.items = Array.from(this.querySelectorAll('.pdp-gallery__desktop .pdp-gallery__item'));
      this.dthumbs = Array.from(this.querySelectorAll('[data-dthumb]'));
      this.onClick = (event) => {
        const zoomImg = event.target.closest('.pdp-gallery__item > img, .pdp-gallery__slide > img');
        if (zoomImg && this.contains(zoomImg)) {
          this.openZoom(zoomImg);
          return;
        }
        const dthumb = event.target.closest('[data-dthumb]');
        if (dthumb && this.contains(dthumb)) {
          this.showDesktop(Number(dthumb.dataset.dthumb));
          return;
        }
        const thumb = event.target.closest('[data-thumb]');
        const nav = event.target.closest('[data-nav]');
        if (thumb && this.contains(thumb)) this.go(Number(thumb.dataset.thumb));
        else if (nav && this.contains(nav)) this.go(this.index + (nav.dataset.nav === 'next' ? 1 : -1));
      };

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.addEventListener('click', this.onClick);
      this.sync();
      this.setupSticky();
    }

    disconnectedCallback() {
      this.track?.removeEventListener('scroll', this.onScroll);
      this.removeEventListener('click', this.onClick);
      cancelAnimationFrame(this.raf);
      this.stickyObserver?.disconnect();
      window.removeEventListener('resize', this.updateSticky);
    }

    // Sticky offset: gallery top = just under the header, or (when the images are taller than the screen) negative so the
    // bottom edge of the images comes into view before they stop moving
    setupSticky() {
      if (!this.classList.contains('pdp-gallery--sticky')) return;
      this.updateSticky = () => {
        const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
        const gap = 16;
        const top = Math.min(header + gap, window.innerHeight - this.offsetHeight - gap);
        this.style.setProperty('--pdp-sticky-top', Math.round(top) + 'px');
      };
      this.stickyObserver = new ResizeObserver(this.updateSticky);
      this.stickyObserver.observe(this);
      window.addEventListener('resize', this.updateSticky, { passive: true });
      this.updateSticky();
    }

    // Click / tap on an image: full screen zoom viewer, opened on the image that was clicked
    openZoom(clicked) {
      const container = clicked.closest('.pdp-gallery__desktop, .pdp-gallery__track');
      const imgs = Array.from(this.querySelectorAll('.pdp-gallery__desktop .pdp-gallery__item > img'));
      const list = imgs.map((img) => ({ src: img.dataset.zoomSrc || img.currentSrc || img.src, alt: img.alt, thumb: img.currentSrc || img.src }));
      const sourceImgs = container && container.matches('.pdp-gallery__track')
        ? Array.from(container.querySelectorAll('.pdp-gallery__slide > img'))
        : imgs;
      const start = Math.max(0, sourceImgs.indexOf(clicked));
      new PdpZoom(list, start, (index) => {
        // keep the page gallery on the image the shopper ended on
        const target = sourceImgs[index];
        if (!target) return;
        const slide = target.closest('.pdp-gallery__slide');
        if (slide && this.track.clientWidth) this.track.scrollTo({ left: this.track.clientWidth * Number(slide.dataset.index), behavior: 'auto' });
        const item = target.closest('.pdp-gallery__item');
        if (item && this.items.length && this.dthumbs.length) this.showDesktop(this.items.indexOf(item));
      });
    }

    // Desktop (thumbnail layouts): show one main image at a time
    showDesktop(i) {
      this.items.forEach((item, n) => {
        const active = n === i;
        item.classList.toggle('is-active', active);
        if (!active) item.querySelectorAll('video').forEach((v) => v.pause());
      });
      this.dthumbs.forEach((thumb, n) => {
        if (n === i) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
    }

    go(i) {
      const last = this.track.children.length - 1;
      const target = Math.max(0, Math.min(last, i));
      this.track.scrollTo({ left: this.track.clientWidth * target, behavior: 'smooth' });
    }

    sync() {
      const width = this.track.clientWidth;
      if (!width) return; // hidden (desktop)

      this.index = Math.round(this.track.scrollLeft / width);
      const last = this.track.children.length - 1;

      this.thumbs.forEach((thumb, n) => {
        if (n === this.index) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });

      const active = this.thumbs[this.index];
      if (active && this.thumbsEl) {
        const left = active.offsetLeft - (this.thumbsEl.clientWidth - active.offsetWidth) / 2;
        this.thumbsEl.scrollTo({ left, behavior: 'smooth' });
      }

      if (this.prev) this.prev.disabled = this.index <= 0;
      if (this.next) this.next.disabled = this.index >= last;
    }
  });
}

/*
 * "{count} parents added this to bag in the last 2 hours".
 * The number is derived from the product id (so every product differs) plus a slow wave over a 2 hour cycle and a
 * small jitter that changes every 10 minutes, so the figure keeps moving while the shopper browses.
 */
if (!customElements.get('ss-pdp-nudge')) {
  customElements.define('ss-pdp-nudge', class extends HTMLElement {
    connectedCallback() {
      this.textEl = this.querySelector('[data-nudge-text]');
      this.min = parseInt(this.dataset.min, 10) || 1;
      this.max = Math.max(this.min, parseInt(this.dataset.max, 10) || this.min);
      this.template = this.dataset.template || '{count} parents added this to bag in the last 2 hours';
      this.pid = parseInt(String(this.dataset.productId).slice(-9), 10) || 1;

      this.render();
      this.timer = setInterval(() => this.render(), 60 * 1000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    // mulberry32: tiny seeded PRNG so the same product + time bucket always gives the same number
    random(seed) {
      let t = (seed + 0x6d2b79f5) >>> 0;
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    }

    count() {
      const span = this.max - this.min;
      const now = Date.now();
      const base = this.min + Math.floor(this.random(this.pid) * (span + 1));
      const phase = this.random(this.pid + 7) * Math.PI * 2;
      const wave = Math.sin((now / (2 * 60 * 60 * 1000)) * Math.PI * 2 + phase) * span * 0.2;
      const bucket = Math.floor(now / (10 * 60 * 1000));
      const jitter = Math.round((this.random(this.pid * 31 + bucket) - 0.5) * Math.max(2, span * 0.12));
      return Math.max(this.min, Math.min(this.max, Math.round(base + wave + jitter)));
    }

    render() {
      if (this.textEl) this.textEl.textContent = this.template.replace('{count}', this.count());
    }
  });
}

/*
 * Offer progress bar: moves the car / fill along the wavy road and updates the message whenever the cart changes.
 * Listens to the theme's own cart events and re-reads /cart.js. Road geometry must match the SVG path in
 * snippets/ss-pdp-offer-progress.liquid.
 */
if (!customElements.get('ss-pdp-offer-progress')) {
  const ROAD = { base: 62, amp: 18, cycles: 1.5, phase: 0.5 };
  const roadY = (u) => ROAD.base + ROAD.amp * Math.sin(2 * Math.PI * ROAD.cycles * u + ROAD.phase);
  const roadSlope = (u, width) => (ROAD.amp * 2 * Math.PI * ROAD.cycles * Math.cos(2 * Math.PI * ROAD.cycles * u + ROAD.phase)) / width;

  customElements.define('ss-pdp-offer-progress', class extends HTMLElement {
    connectedCallback() {
      try { this.tiers = JSON.parse(this.dataset.tiers || '[]'); } catch (e) { this.tiers = []; }
      this.tiers.sort((a, b) => a.threshold - b.threshold);

      this.metric = this.dataset.metric === 'quantity' ? 'quantity' : 'amount';
      this.basis = this.dataset.basis || 'items_subtotal_price';
      this.start = parseFloat(this.dataset.start) || 45;
      this.productId = parseInt(this.dataset.productId, 10);
      this.countProduct = this.dataset.countProduct === 'true';
      this.productPrice = (parseInt(this.dataset.productPrice, 10) || 0) / 100;
      this.currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'INR';

      // Gamified Buy 1 Get 1 mode: only road + car + diya (see snippets/ss-pdp-offer-progress-bogo.liquid)
      this.bogo = this.dataset.mode === 'bogo';
      this.bogoTarget = parseInt(this.dataset.bogoTarget, 10) || 2;
      this.carW = parseFloat(this.dataset.carWidth) || 70;
      this.diyaSize = parseFloat(this.dataset.diyaSize) || 56;
      this.roadSize = parseFloat(this.dataset.roadSize) || 60;
      this.baseCarW = this.carW;
      this.baseDiya = this.diyaSize;
      this.baseStart = this.start;
      this.diya = this.querySelector('[data-opb-diya]');
      this.srEl = this.querySelector('[data-opb-sr]');
      this.won = this.classList.contains('is-won');

      this.scale = this.querySelector('.pdp-opb__scale');
      this.fill = this.querySelector('[data-opb-fill]');
      this.car = this.querySelector('.pdp-opb__car');
      this.valueEl = this.querySelector('[data-opb-value]');
      this.pill = this.querySelector('.pdp-opb__pill');
      this.messageEl = this.querySelector('[data-opb-message]');
      this.tierEls = Array.from(this.querySelectorAll('[data-opb-tier]'));

      this.f = parseFloat(this.style.getPropertyValue('--f')) || 0;
      this.target = this.f;

      this.onCartChange = () => {
        clearTimeout(this.debounce);
        this.debounce = setTimeout(() => this.refresh(), 150);
      };
      document.documentElement.addEventListener('cart:updated', this.onCartChange);
      document.documentElement.addEventListener('cart:refresh', this.onCartChange);

      this.resizeObserver = new ResizeObserver(() => this.place(this.f));
      this.resizeObserver.observe(this.scale);

      this.place(this.f);
      this.refresh(true);
    }

    disconnectedCallback() {
      document.documentElement.removeEventListener('cart:updated', this.onCartChange);
      document.documentElement.removeEventListener('cart:refresh', this.onCartChange);
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.raf);
      clearTimeout(this.debounce);
      clearTimeout(this.winTimer);
      clearTimeout(this.celebrateTimer);
    }

    async refresh(first) {
      try {
        const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
        const response = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } });
        this.render(await response.json(), first);
      } catch (e) { /* keep the server-rendered state */ }
    }

    money(value) {
      const locale = this.currency === 'INR' ? 'en-IN' : undefined;
      try {
        return new Intl.NumberFormat(locale, { style: 'currency', currency: this.currency, maximumFractionDigits: 0 }).format(value);
      } catch (e) { return String(Math.round(value)); }
    }

    format(value) {
      if (this.metric === 'quantity') return value + ' ' + (value === 1 ? 'item' : 'items');
      return this.money(value);
    }

    // Puts the car, the fill and the flags on the road for a progress fraction f (0..1)
    place(f) {
      const width = this.scale.clientWidth;
      if (!width) return;
      // in BOGO mode the car stops just before the diya at the end of the road
      const endX = this.bogo ? Math.max(this.start, width - this.diyaSize - 8 - this.carW / 2) : width;
      const xOf = (fraction) => this.start + (endX - this.start) * fraction;
      let k = parseFloat(getComputedStyle(this).getPropertyValue('--opb-k')) || 1;
      if (this.bogo) {
        // BOGO: road, car and diya shrink together with the card on small screens
        const sc = Math.min(1, width / 420);
        k = 0.9 * (this.roadSize / 100) * sc;
        this.carW = this.baseCarW * sc;
        this.diyaSize = this.baseDiya * sc;
        this.start = this.carW / 2 + 6;
        this.style.setProperty('--opb-k', k.toFixed(4));
        this.style.setProperty('--opb-car-width', this.carW.toFixed(1) + 'px');
        this.style.setProperty('--opb-diya', this.diyaSize.toFixed(1) + 'px');
        this.style.setProperty('--opb-start', this.start.toFixed(1) + 'px');
      }

      this.f = f;
      const x = xOf(f);
      // no yellow road at all while the cart is empty (0 progress)
      const filled = f > 0.0005 ? (this.bogo && f >= 0.999 ? width : x) : 0;
      if (this.fill) this.fill.style.clipPath = 'inset(0 ' + (width - filled) + 'px 0 0)';
      if (this.car) {
        const u = x / width;
        const angle = Math.atan(roadSlope(u, width) * k) * (180 / Math.PI);
        this.car.style.left = x + 'px';
        this.car.style.top = roadY(u) * k + 'px';
        this.car.style.transform = 'translate(-50%, -78%) rotate(' + angle.toFixed(2) + 'deg)';
      }
      if (this.diya) {
        const dx = width - this.diyaSize / 2 - 6;
        this.diya.style.left = dx + 'px';
        this.diya.style.top = roadY(dx / width) * k + 'px';
      }
      this.tierEls.forEach((el) => {
        const tx = xOf(parseFloat(el.dataset.f));
        el.style.left = tx + 'px';
        const ty = roadY(tx / width) * k;
        el.style.top = ty + 'px';
        el.style.setProperty('--dy', ty + 'px'); // flags / texts all start on the same line below the road
      });
    }

    // Offers are evenly spaced, so progress is linear between the previous and the next offer
    fractionFor(value) {
      let prevT = 0;
      let prevF = 0;
      for (const tier of this.tiers) {
        if (value < tier.threshold) return prevF + ((value - prevT) / (tier.threshold - prevT)) * (tier.f - prevF);
        prevT = tier.threshold;
        prevF = tier.f;
      }
      return prevF;
    }

    animateTo(target) {
      cancelAnimationFrame(this.raf);
      this.classList.add('is-driving');
      const from = this.f;
      const began = performance.now();
      const duration = matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 1000;
      const step = (now) => {
        const t = duration ? Math.min(1, (now - began) / duration) : 1;
        const eased = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
        this.place(from + (target - from) * eased);
        if (t < 1) this.raf = requestAnimationFrame(step);
        else this.classList.remove('is-driving');
      };
      this.raf = requestAnimationFrame(step);
    }

    // BOGO mode: the car drives one step per item, the diya lights up when enough items are in the bag
    renderBogo(cart, first) {
      let value = cart.item_count;
      const inCart = cart.items.some((item) => item.product_id === this.productId);
      if (this.countProduct && !inCart) value += 1;

      const target = Math.min(1, value / this.bogoTarget);
      if (first) this.place(target); else this.animateTo(target);
      this.classList.toggle('is-empty', target === 0);

      const won = target >= 1;
      if (this.srEl) {
        const key = won ? 'msgWon' : target > 0 ? 'msgProgress' : 'msgStart';
        this.srEl.textContent = this.dataset[key] || '';
      }

      clearTimeout(this.winTimer);
      if (won && !this.won) {
        this.won = true;
        // light the diya when the car has arrived
        this.winTimer = setTimeout(() => {
          this.classList.add('is-won', 'is-celebrate');
          clearTimeout(this.celebrateTimer);
          this.celebrateTimer = setTimeout(() => this.classList.remove('is-celebrate'), 2000);
        }, first ? 0 : 950);
      } else if (!won && this.won) {
        this.won = false;
        clearTimeout(this.celebrateTimer);
        this.classList.remove('is-won', 'is-celebrate');
      }
    }

    render(cart, first) {
      if (this.bogo) { this.renderBogo(cart, first); return; }
      let value;
      const inCart = cart.items.some((item) => item.product_id === this.productId);
      if (this.metric === 'quantity') {
        value = cart.item_count;
      } else {
        value = (this.basis === 'original_total_price' ? cart.original_total_price : cart.items_subtotal_price) / 100;
        if (this.countProduct && !inCart) value += this.productPrice;
      }

      const target = this.fractionFor(value);
      if (first) this.place(target); else this.animateTo(target);

      if (this.valueEl) this.valueEl.textContent = this.format(value);
      this.tierEls.forEach((el) => el.classList.toggle('is-reached', value >= parseFloat(el.dataset.opbTier)));

      const next = this.tiers.find((tier) => tier.threshold > value);
      if (this.messageEl) {
        const template = next ? this.dataset.message : this.dataset.unlockedMessage;
        const text = (template || '')
          .replace('{remaining}', next ? this.format(next.threshold - value) : '')
          .replace('{offer}', next ? next.unlock : '');
        this.messageEl.textContent = text;
        if (this.pill) {
          this.pill.hidden = !text.trim();
          this.pill.classList.toggle('is-complete', !next);
        }
      }
    }
  });
}

/*
 * Mobile sticky bar (snippets/ss-pdp-sticky-bar.liquid):
 * - Subtotal = selected variant price x quantity, quantity stays in sync with the quantity box of the page
 * - "Add To Bag" / "Buy it now" open the "Select variant" sheet; tapping a size adds the product with that quantity
 *   (Add To Bag -> theme cart drawer opens, Buy it now -> checkout)
 * - the bar stays visible on the whole product page (mobile)
 */
if (!customElements.get('ss-pdp-sticky-bar')) {
  customElements.define('ss-pdp-sticky-bar', class extends HTMLElement {
    connectedCallback() {
      let data = {};
      try { data = JSON.parse(this.querySelector('[data-sb-data]').textContent); } catch (e) { return; }
      this.data = data;
      this.formId = this.dataset.formId;
      this.sizePos = parseInt(this.dataset.sizePosition, 10) || 0;
      this.onlyDefault = this.dataset.onlyDefault === 'true';
      this.qty = 1;
      this.mode = 'add';
      this.busy = false;
      this.currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'INR';

      this.subtotalEl = this.querySelector('[data-sb-subtotal]');
      this.qtyEl = this.querySelector('[data-sb-qty]');
      this.sheet = this.querySelector('[data-sb-sheet]');
      this.chipsEl = this.querySelector('[data-sb-chips]');
      this.hintEl = this.querySelector('[data-sb-hint]');
      this.hintDefault = this.hintEl.textContent;

      this.querySelector('[data-sb-minus]').addEventListener('click', () => this.setQty(this.qty - 1, true));
      this.querySelector('[data-sb-plus]').addEventListener('click', () => this.setQty(this.qty + 1, true));
      this.querySelectorAll('[data-sb-action]').forEach((btn) => btn.addEventListener('click', () => this.openSheet(btn.dataset.sbAction)));
      this.querySelectorAll('[data-sb-close]').forEach((el) => el.addEventListener('click', () => this.closeSheet()));

      this.onKey = (event) => { if (event.key === 'Escape') this.closeSheet(); };
      this.onSync = () => this.sync();
      document.addEventListener('keydown', this.onKey);
      document.addEventListener('variant:changed', this.onSync);
      document.addEventListener('change', this.onSync);
      document.addEventListener('input', this.onSync);

      this.sync();
      this.hidden = false;
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKey);
      document.removeEventListener('variant:changed', this.onSync);
      document.removeEventListener('change', this.onSync);
      document.removeEventListener('input', this.onSync);
      document.documentElement.classList.remove('pdp-sb-lock');
    }

    money(cents) {
      const value = cents / 100;
      const locale = this.currency === 'INR' ? 'en-IN' : undefined;
      try {
        return new Intl.NumberFormat(locale, { style: 'currency', currency: this.currency, minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(value);
      } catch (e) { return String(value); }
    }

    get form() { return document.getElementById(this.formId); }

    get mainQtyInput() { return document.querySelector('input[name="quantity"][form="' + this.formId + '"]'); }

    // the variant currently selected on the page
    currentVariant() {
      const form = this.form;
      const id = form && form.elements && form.elements.id ? parseInt(form.elements.id.value, 10) : parseInt(this.dataset.selectedVariant, 10);
      return this.data.variants.find((v) => v.id === id) || this.data.variants.find((v) => v.available) || this.data.variants[0];
    }

    setQty(value, push) {
      const input = this.mainQtyInput;
      const max = input && input.max ? parseInt(input.max, 10) : 99;
      this.qty = Math.max(1, Math.min(max || 99, value || 1));
      if (push && input) {
        input.value = this.qty;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.render();
    }

    // read the quantity box of the page (when it exists) and refresh the subtotal
    sync() {
      const input = this.mainQtyInput;
      if (input) {
        const fromPage = parseInt(input.value, 10);
        if (fromPage >= 1) this.qty = fromPage;
      }
      this.render();
      if (!this.sheet.hidden) this.renderSheet();
    }

    render() {
      const variant = this.currentVariant();
      this.qtyEl.textContent = this.qty;
      if (variant) this.subtotalEl.textContent = this.money(variant.price * this.qty);
    }

    openSheet(mode) {
      this.mode = mode;
      this.hintEl.textContent = this.hintDefault;
      this.hintEl.classList.remove('is-error');
      this.renderSheet();
      this.sheet.hidden = false;
      document.documentElement.classList.add('pdp-sb-lock');
      requestAnimationFrame(() => this.sheet.classList.add('is-open'));
    }

    closeSheet() {
      if (this.sheet.hidden) return;
      this.sheet.classList.remove('is-open');
      document.documentElement.classList.remove('pdp-sb-lock');
      setTimeout(() => { if (!this.sheet.classList.contains('is-open')) this.sheet.hidden = true; }, 220);
    }

    renderSheet() {
      const current = this.currentVariant();
      const thumb = this.querySelector('[data-sb-thumb]');
      if (thumb) thumb.src = (current && current.image) || this.data.image || thumb.src;
      this.querySelector('[data-sb-price]').textContent = current ? this.money(current.price) : '';
      const compare = this.querySelector('[data-sb-compare]');
      const off = this.querySelector('[data-sb-off]');
      if (current && current.compare > current.price) {
        compare.textContent = this.money(current.compare);
        off.textContent = '(' + Math.round(((current.compare - current.price) * 100) / current.compare) + '% OFF)';
      } else { compare.textContent = ''; off.textContent = ''; }

      this.querySelector('[data-sb-label]').textContent = this.onlyDefault ? '' : this.data.sizeLabel;
      this.chipsEl.innerHTML = '';
      if (this.onlyDefault) {
        this.hintEl.textContent = 'Tap to continue';
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'pdp-sb__chip';
        chip.textContent = this.mode === 'buy' ? 'Buy it now' : 'Add to bag';
        chip.disabled = !current.available;
        chip.addEventListener('click', () => this.choose(current));
        this.chipsEl.appendChild(chip);
        return;
      }

      // sizes (option at sizePos) in their original order; every other option keeps the page's current choice
      const idx = this.sizePos - 1;
      const sizes = [];
      this.data.variants.forEach((v) => { if (sizes.indexOf(v.options[idx]) === -1) sizes.push(v.options[idx]); });
      sizes.forEach((size) => {
        const matches = this.data.variants.filter((v) => v.options[idx] === size && v.options.every((val, i) => i === idx || !current || val === current.options[i]));
        const variant = matches[0] || this.data.variants.find((v) => v.options[idx] === size);
        const available = (matches.length ? matches : [variant]).some((v) => v && v.available);
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'pdp-sb__chip' + (available ? '' : ' is-sold-out');
        chip.textContent = size;
        chip.disabled = !available;
        chip.addEventListener('click', () => this.choose(matches.find((v) => v.available) || variant));
        this.chipsEl.appendChild(chip);
      });
    }

    async choose(variant) {
      if (this.busy || !variant) return;
      this.busy = true;
      this.sheet.classList.add('is-busy');
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      const routes = (window.themeVariables && window.themeVariables.routes) || {};
      try {
        const body = new FormData();
        body.append('id', variant.id);
        body.append('quantity', this.qty);
        body.append('sections', 'mini-cart');
        const response = await fetch((routes.cartAddUrl || root + 'cart/add') + '.js', { method: 'POST', body, headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        const json = await response.json();
        if (!response.ok) throw new Error(json.description || json.message || 'Could not add to bag');

        if (this.mode === 'buy') {
          window.location.href = root + 'checkout';
          return;
        }

        const cartType = window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.cartType;
        if (cartType === 'page') { window.location.href = root + 'cart'; return; }

        this.closeSheet();
        const cart = await (await fetch((routes.cartUrl || root + 'cart') + '.js')).json();
        document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart } }));
        cart.sections = json.sections;
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart, openMiniCart: cartType === 'drawer' } }));
        this.dispatchEvent(new CustomEvent('cart-notification:show', { bubbles: true, cancelable: true, detail: { status: 'success', error: '' } }));
      } catch (error) {
        this.hintEl.textContent = error.message;
        this.hintEl.classList.add('is-error');
      } finally {
        this.busy = false;
        this.sheet.classList.remove('is-busy');
      }
    }
  });
}
/*
 * Keeps the "Size Chart" link when the variant changes.
 * Picking a size makes the theme swap the whole variant picker for freshly rendered HTML; if the link is missing from
 * that HTML it vanishes. We remember the link(s) from the first render and put them back into the matching option row.
 * (The drawer / popover they open live outside the picker, so they are not affected.)
 */
(function () {
  const LINK = '.product-form__option-link';
  const remembered = new Map(); // option name -> [link elements]

  const nameOf = (selector) => {
    const el = selector.querySelector('.product-form__option-name');
    return el ? el.textContent.replace(/^\s*select\s+/i, '').replace(/:\s*$/, '').trim().toLowerCase() : '';
  };

  const remember = () => {
    document.querySelectorAll('.product-form__option-selector').forEach((selector) => {
      const links = Array.from(selector.querySelectorAll(LINK));
      const name = nameOf(selector);
      if (links.length && name && !remembered.has(name)) remembered.set(name, links.map((link) => link.cloneNode(true)));
    });
  };

  const restore = () => {
    document.querySelectorAll('.product-form__option-selector').forEach((selector) => {
      const links = remembered.get(nameOf(selector));
      const info = selector.querySelector('.product-form__option-info');
      if (links && info && !info.querySelector(LINK)) links.forEach((link) => info.appendChild(link.cloneNode(true)));
    });
  };

  /*
   * Apps (Kiwi Size Chart) inject their own elements into the option row (.ks-chart-container, .ks-calculator-container).
   * When the picker is swapped these are removed with the old picker and the app does not add them again.
   * We keep the ORIGINAL elements (so their click handlers still work) and move them into the new picker.
   */
  const APP = '.ks-chart-container, .ks-calculator-container';
  const kept = new Map(); // option name -> [original app elements]

  const keepRemoved = (records) => {
    records.forEach((record) => {
      record.removedNodes.forEach((node) => {
        if (node.nodeType !== 1 || !node.querySelectorAll) return;
        (node.matches('.product-form__option-selector') ? [node] : Array.from(node.querySelectorAll('.product-form__option-selector'))).forEach((selector) => {
          const nodes = Array.from(selector.querySelectorAll(APP));
          const name = nameOf(selector);
          if (nodes.length && name) kept.set(name, nodes);
        });
      });
    });
  };

  const restoreApp = () => {
    document.querySelectorAll('.product-form__option-selector').forEach((selector) => {
      const nodes = kept.get(nameOf(selector));
      const info = selector.querySelector('.product-form__option-info');
      if (nodes && info && !selector.querySelector(APP)) {
        info.append(...nodes); // inside the row, so it sits on the right (the row is a flex row)
        kept.delete(nameOf(selector));
      }
    });
  };

  const start = () => {
    remember();
    let queued = false;
    let pending = [];
    new MutationObserver((records) => {
      pending.push(...records);
      if (queued) return;
      queued = true;
      setTimeout(() => {
        queued = false;
        const batch = pending;
        pending = [];
        keepRemoved(batch);
        remember();
        restore();
        restoreApp();
      }, 0);
    }).observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})();